import React from 'react';
import PropTypes from 'prop-types';

import audioSinkService from '../../../../services/livesession/AudioSinkService';
import micMuter from '../../../../services/livesession/MicMuter';

/**
 * SoundButton is a generic component that disables a button during playback.
 *
 * For the button prop, it expects a component class/function that it will
 * render with a disabled prop during playback, and a onClick prop to start
 * playback.
 *
 * SoundButton expects a single <source /> element or a list of <source />
 * elements as children elements.
 *
 * It allows the component to be agnostic of the URL of the media resource.
 *
 * speakerDeviceId prop should be a device ID as defined in Audio Output API.
 *
 * userMediaStream prop is used to temporarily mute the microphone during audio playback
 * to prevent the test sound from being picked up by the microphone.
 */
class SoundButton extends React.PureComponent {
    static propTypes = {
        button: PropTypes.elementType.isRequired,
        children: PropTypes.oneOfType([
            PropTypes.element,
            PropTypes.arrayOf(PropTypes.element),
        ]).isRequired,
        speakerDeviceId: PropTypes.string,
        userMediaStream: PropTypes.object,
        shouldMuteMicWhenSoundTestIsOn: PropTypes.bool,
    };

    static defaultProps = {
        speakerDeviceId: null,
        userMediaStream: null,
        shouldMuteMicWhenSoundTestIsOn: false,
    };

    constructor(props) {
        super(props);

        this.audioRef = React.createRef();
        this.state = {
            isPlaying: false,
        };
    }

    componentDidMount() {
        this.setSinkId();
    }

    componentDidUpdate(prevProps) {
        this.setSinkId();

        const { userMediaStream, shouldMuteMicWhenSoundTestIsOn } = this.props;
        const { isPlaying } = this.state;
        const streamChanged = prevProps.userMediaStream !== userMediaStream;

        if (streamChanged && shouldMuteMicWhenSoundTestIsOn) {
            // When the stream changes, always unmute the previous one.
            if (prevProps.userMediaStream) {
                micMuter.unmuteMicrophone(prevProps.userMediaStream);
            }

            // If audio is currently playing, mute the new stream.
            if (isPlaying && userMediaStream) {
                micMuter.muteMicrophone(userMediaStream);
            }
        }
    }

    /**
     * Checks for a valid userMediaStream in the component's props and, if it exists,
     * calls the MicMuter service to mute the microphone.
     */
    muteMicrophone() {
        const { userMediaStream, shouldMuteMicWhenSoundTestIsOn } = this.props;
        if (userMediaStream && shouldMuteMicWhenSoundTestIsOn) {
            micMuter.muteMicrophone(userMediaStream);
        }
    }

    /**
     * Checks for a valid userMediaStream in the component's props and, if it exists,
     * calls the MicMuter service to restore the microphone to its original state.
     */
    unmuteMicrophone() {
        const { userMediaStream, shouldMuteMicWhenSoundTestIsOn } = this.props;
        if (userMediaStream && shouldMuteMicWhenSoundTestIsOn) {
            micMuter.unmuteMicrophone(userMediaStream);
        }
    }

    setSinkId() {
        audioSinkService.setSinkId(
            this.audioRef.current,
            this.props.speakerDeviceId,
        );
    }

    onAudioStatusChange = (theEvent) => {
        const audio = theEvent.target;
        const isNowPlaying = !audio.paused;

        // If the audio is now playing and wasn't playing before, mute the microphone
        if (isNowPlaying && !this.state.isPlaying) {
            this.muteMicrophone();
        // If the audio is now paused and was playing before, unmute the microphone
        } else if (!isNowPlaying && this.state.isPlaying) {
            this.unmuteMicrophone();
        }

        this.setState({
            isPlaying: isNowPlaying,
        });
    };

    play = () => {
        const audio = this.audioRef.current;

        if (!audio) {
            return;
        }

        audio.play();
    };

    render() {
        const { button: ButtonComponent, children } = this.props;
        const { isPlaying } = this.state;

        return (
            <React.Fragment>
                <ButtonComponent disabled={isPlaying} onClick={this.play} />
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio
                    onEnded={this.onAudioStatusChange}
                    onPause={this.onAudioStatusChange}
                    onPlay={this.onAudioStatusChange}
                    preload="auto"
                    ref={this.audioRef}
                >
                    {children}
                </audio>
            </React.Fragment>
        );
    }
}

export default SoundButton;
